import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { useConfig } from "../../providers/ConfigProvider";
import { useProject } from "../../providers/ProjectProvider";
import { useBreadcrumbs, useFindRouteComponent } from "../../providers/RoutesProvider";
import { cn } from "../../utils/bem";
import { absoluteURL } from "../../utils/helpers";
import { Dropdown } from "@humansignal/ui";
import { Menu } from "../Menu/Menu";
import { useTranslation } from "../../i18n/LocaleProvider";
import "./Breadcrumbs.prefix.css";

const block = cn("breadcrumbs");

// Breadcrumb titles (especially project / org names AND multi-word
// page titles like "Labeling Interface" / "Cloud Storage" / "Hugging
// Face Hub") get long enough to push the rest of the trail off-screen.
// Trim aggressively — 14 chars keeps the compact breadcrumb + ProjectNav
// + bell + user avatar comfortable even at 320px wide. Full title
// still lands in the ``title`` attribute so a hover reveals the whole
// name.
const BREADCRUMB_MAX_CHARS = 14;
const trimCrumbTitle = (title) => {
  if (typeof title !== "string") return title;
  const trimmed = title.trim();
  if (trimmed.length <= BREADCRUMB_MAX_CHARS) return trimmed;
  return `${trimmed.slice(0, BREADCRUMB_MAX_CHARS - 1).trimEnd()}…`;
};

// Short-form overrides for known page titles whose full form eats a
// lot of horizontal space in the breadcrumb bar. Applied AFTER
// localisation so operator translations still win; only kicks in when
// the current page's title matches a known long English label. Keeps
// crumb width predictable regardless of active locale.
const SHORT_TITLE_OVERRIDES = {
  "Labeling Interface": "Labeling",
  "Cloud Storage": "Storage",
  "Hugging Face Hub": "HF Hub",
  "Label Distribution": "Labels",
  "New Organization": "New Org",
  "Data Manager": "Data",
  "Machine Learning": "ML",
  Predictions: "Predict",
  Notifications: "Alerts",
  "Danger Zone": "Danger",
  "Project templates": "Templates",
};
const applyShortOverride = (title) => {
  if (typeof title !== "string") return title;
  return SHORT_TITLE_OVERRIDES[title.trim()] ?? title;
};

// Page titles are set as static strings on each Page component
// (``HomePage.title = "Home"`` etc.) so the breadcrumb crumbs come
// through in English regardless of active locale. Map each known
// English title to its translation key; everything not in the map
// (project names, org names, dynamic crumbs) passes through unchanged.
const TITLE_TO_KEY = {
  Home: "nav.home",
  Projects: "nav.projects",
  Organization: "nav.organization",
  Platform: "nav.platform",
  Settings: "nav.settings",
  Members: "nav.members",
  Dashboard: "nav.dashboard",
  "Data Manager": "nav.data_manager",
  Analytics: "nav.analytics",
  Notifications: "nav.notifications",
  General: "breadcrumbs.titles.general",
  Review: "breadcrumbs.titles.review",
  "Review queue": "breadcrumbs.titles.review_queue",
  Security: "breadcrumbs.titles.security",
  Metrics: "breadcrumbs.titles.metrics",
  Consent: "breadcrumbs.titles.consent",
  Annotation: "breadcrumbs.titles.annotation",
  "Audio QC": "breadcrumbs.titles.audio_qc",
  Licensing: "breadcrumbs.titles.licensing",
  "Danger Zone": "breadcrumbs.titles.danger_zone",
  "Labeling Interface": "breadcrumbs.titles.labeling_interface",
  Predictions: "breadcrumbs.titles.predictions",
  Quality: "breadcrumbs.titles.quality",
  Model: "breadcrumbs.titles.model",
  Models: "breadcrumbs.titles.models",
  "Cloud Storage": "breadcrumbs.titles.cloud_storage",
  "Project templates": "breadcrumbs.titles.project_templates",
  People: "breadcrumbs.titles.people",
  Activity: "breadcrumbs.titles.activity",
  "Label Distribution": "breadcrumbs.titles.label_distribution",
  "New Organization": "breadcrumbs.titles.new_organization",
};
const localiseTitle = (t, title) => {
  if (typeof title !== "string") return title;
  const key = TITLE_TO_KEY[title.trim()];
  return key ? t(key, undefined, title) : title;
};

// Decide whether the current user should see a "Data Manager" crumb
// for this project. Mirrors the ProjectNav logic so the breadcrumb
// never advertises a destination the user will be bounced from.
const userCanSeeDataManager = (user, project) => {
  if (!user) return true; // unauthenticated → let the guard decide
  const role = user.active_organization_role;
  const canManage =
    !!user.is_staff ||
    !!user.is_superuser ||
    ["OW", "AD", "MA"].includes(role) ||
    (project && project.created_by?.id === user.id);
  if (canManage) return true;
  if (!project) return true; // project payload still loading — don't hide prematurely
  const annotatorsSeeDM = project.annotators_see_data_manager ?? true;
  const reviewersSeeDM = project.reviewers_see_data_manager ?? true;
  if (role === "RE") return reviewersSeeDM;
  return annotatorsSeeDM;
};

// Breadcrumb visibility rule for project-scoped routes:
//
//   • Pages WITH sub-pages (only Settings today) — show the breadcrumb.
//     The trail becomes the wayfinding tool the user reaches for when
//     they need to jump BETWEEN Settings sub-pages via the SidebarMenu
//     picker OR back UP to the project / org.
//   • Pages WITHOUT sub-pages (Dashboard, Data Manager, Members) — hide
//     the breadcrumb. Their ProjectNav pill row already highlights the
//     current tab; a breadcrumb trail ending in the same word is
//     visible clutter with no additional wayfinding value.
//
// Non-project routes (Home / Organization / Platform) keep the
// breadcrumb unconditionally — they have no ProjectNav to compete with.
const PROJECT_SCOPED_PATH = /^\/projects\/\d+/;
const SETTINGS_ANY_PATH = /^\/projects\/\d+\/settings(\/|$)/;
const SETTINGS_BASE_PATH = /^\/projects\/\d+\/settings\/?$/;
const isProjectScopedRoute = (pathname) => PROJECT_SCOPED_PATH.test(pathname);
const isSettingsAnyRoute = (pathname) => SETTINGS_ANY_PATH.test(pathname);
const isSettingsBaseRoute = (pathname) => SETTINGS_BASE_PATH.test(pathname);

export const Breadcrumbs = () => {
  const { t } = useTranslation();
  const config = useConfig();
  const reactBreadcrumbs = useBreadcrumbs();
  const findComponent = useFindRouteComponent();
  const { user } = useAuth();
  const { project } = useProject();
  const location = useLocation();
  const [breadcrumbs, setBreadcrumbs] = useState(reactBreadcrumbs);

  // Filter rule: hide the "Data Manager" crumb for annotators /
  // reviewers whose project disabled DM visibility via settings.
  // Same check as the top ProjectNav uses, so either both show or
  // both hide — no accidental "dead link" trail items.
  const dmVisible = userCanSeeDataManager(user, project);
  const visibleBreadcrumbs = useMemo(() => {
    if (dmVisible) return breadcrumbs;
    return breadcrumbs.filter((c) => c.title !== "Data Manager");
  }, [breadcrumbs, dmVisible]);

  useEffect(() => {
    if (reactBreadcrumbs.length) {
      setBreadcrumbs(reactBreadcrumbs);
    } else if (config.breadcrumbs) {
      setBreadcrumbs(config.breadcrumbs);
    }
  }, [reactBreadcrumbs, config]);

  // Base Settings URL — no route entry for GeneralSettings so the
  // trail ends at "Settings" and the leaf label is missing. Append
  // a synthetic "General" crumb so the base page reads consistently
  // with sub-pages like "… / Settings / Labeling". Use ``t()`` so
  // the injected crumb localises alongside the rest.
  const trailBreadcrumbs = useMemo(() => {
    if (!isSettingsBaseRoute(location.pathname)) return visibleBreadcrumbs;
    const alreadyHasGeneral =
      visibleBreadcrumbs.length > 0 && visibleBreadcrumbs[visibleBreadcrumbs.length - 1].title === "General";
    if (alreadyHasGeneral) return visibleBreadcrumbs;
    return [...visibleBreadcrumbs, { title: "General", key: "settings-general-synthetic", path: location.pathname }];
  }, [visibleBreadcrumbs, location.pathname]);

  // Suppress the breadcrumb on project-scoped routes with no sub-pages
  // (Dashboard, Data Manager, Members). Done post-hooks so hook order
  // stays stable across pathname changes.
  if (isProjectScopedRoute(location.pathname) && !isSettingsAnyRoute(location.pathname)) {
    return null;
  }

  const currentCrumb = trailBreadcrumbs[trailBreadcrumbs.length - 1];
  const parentCrumbs = trailBreadcrumbs.slice(0, -1);

  // ── Compact (narrow-screen) form ──
  // Classic pro-site breadcrumb collapse: render just the current
  // page title + a dropdown arrow; clicking opens a menu listing
  // every parent crumb as a navigable link. Visibility is CSS-gated
  // (see ``.breadcrumbs__compact`` + ``.breadcrumbs__list`` rules)
  // so the markup is always present for SSR / a11y and the browser
  // decides which tree to paint based on viewport width.
  const compact = (
    <div className={block.elem("compact").toClassName()}>
      {parentCrumbs.length > 0 ? (
        // Plain JSX as content — ``Dropdown.Trigger`` already wraps it
        // in its own <Dropdown> internally, so wrapping again produces
        // nested Dropdowns that never toggle visible.
        <Dropdown.Trigger
          content={
            <Menu>
              {parentCrumbs.map((item, i) => {
                const href = item.href ?? item.path;
                // Prefer SPA navigation (``to``) for internal paths so
                // clicking a crumb doesn't trigger a full page reload;
                // external URLs fall back to the plain ``href``.
                //
                // ``exact`` prevents MenuItem's prefix-based active
                // detection (``pathname.includes(url)``) from marking
                // every parent crumb as the "current page" — which was
                // killing clicks because the active CSS rule applies
                // ``pointer-events: none`` (see Menu.prefix.css).
                // Parent crumbs are never the current page by
                // definition, so exact match is the correct semantic
                // and the items stay clickable.
                const isInternal = typeof href === "string" && href.startsWith("/") && findComponent(href) !== null;
                const localised = localiseTitle(t, item.title);
                return (
                  <Menu.Item
                    key={`compact-${i}-${item.title}`}
                    label={trimCrumbTitle(applyShortOverride(localised))}
                    title={typeof localised === "string" ? localised : undefined}
                    to={isInternal ? href : undefined}
                    href={isInternal ? undefined : (href ?? undefined)}
                    onClick={item.onClick}
                    exact
                  />
                );
              })}
            </Menu>
          }
        >
          <button
            type="button"
            className={block.elem("compact-trigger").toClassName()}
            aria-label={t("components.breadcrumbs.breadcrumbs.show_breadcrumb_path")}
            title={typeof currentCrumb?.title === "string" ? localiseTitle(t, currentCrumb.title) : undefined}
          >
            {/* Path-style prefix — e.g. ``../consent`` — signals visually
                that the current label is a leaf and there IS a parent
                trail to open. The prefix is decorative; the whole
                button opens the parent-crumbs dropdown when tapped. */}
            <span className={block.elem("compact-prefix").toClassName()} aria-hidden>
              ../
            </span>
            <span className={block.elem("compact-title").toClassName()}>
              {trimCrumbTitle(applyShortOverride(localiseTitle(t, currentCrumb?.title ?? "")))}
            </span>
            <svg
              className={block.elem("compact-caret").toClassName()}
              width="10"
              height="10"
              viewBox="0 0 12 12"
              fill="none"
              aria-hidden
            >
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </Dropdown.Trigger>
      ) : (
        <span
          className={block.elem("compact-title").toClassName()}
          title={typeof currentCrumb?.title === "string" ? localiseTitle(t, currentCrumb.title) : undefined}
        >
          {trimCrumbTitle(applyShortOverride(localiseTitle(t, currentCrumb?.title ?? "")))}
        </span>
      )}
    </div>
  );

  return (
    <div className={block.toClassName()}>
      <ul className={block.elem("list").toClassName()}>
        {trailBreadcrumbs.map((item, index, list) => {
          const isLastItem = index === list.length - 1;

          const key = `item-${index}-${item.title}`;

          const href = item.href ?? item.path;

          const isInternal = findComponent(href) !== null;

          const localisedTitle = localiseTitle(t, item.title);
          const title = (
            <span
              className={block
                .elem("label")
                .mod({ faded: index === item.length - 1 })
                .toClassName()}
              title={typeof localisedTitle === "string" ? localisedTitle : undefined}
            >
              {trimCrumbTitle(applyShortOverride(localisedTitle))}
            </span>
          );

          const dropdownSubmenu = item.submenu ? (
            <Dropdown>
              <Menu>
                {item.submenu.map((sub, index) => {
                  return (
                    <Menu.Item
                      key={`${index}-${item.title}`}
                      label={sub.title}
                      icon={sub.icon}
                      href={sub.href ?? sub.path}
                      active={sub.active}
                    />
                  );
                })}
              </Menu>
            </Dropdown>
          ) : null;

          return item.onClick ? (
            <li key={key} className={block.elem("item").mod({ last: isLastItem }).toClassName()}>
              <span onClick={item.onClick}>{title}</span>
            </li>
          ) : dropdownSubmenu ? (
            <Dropdown.Trigger
              key={key}
              component="li"
              className={block.elem("item").mod({ last: isLastItem }).toClassName()}
              content={dropdownSubmenu}
            >
              <span>{title}</span>
            </Dropdown.Trigger>
          ) : href && !isLastItem ? (
            <li key={key} className={block.elem("item").mod({ last: isLastItem }).toClassName()}>
              {isInternal ? (
                <NavLink to={href} data-external={true}>
                  {title}
                </NavLink>
              ) : (
                <a href={absoluteURL(href)}>{title}</a>
              )}
            </li>
          ) : (
            <li key={key} className={block.elem("item").mod({ last: isLastItem }).toClassName()}>
              {title}
            </li>
          );
        })}
      </ul>
      {compact}
    </div>
  );
};
